import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useLanguage } from "../../utils/languageContext.js";
import { getTranslation } from "../../utils/translate.js";
import LoadingSpinner from "../../components/LoadingSpinner/loadingSpinner.js";
import chatService from "../../services/chat-service.js";
import { getActiveOrdersByCustomer } from "../../services/order-service.js";
import Toast from "../../components/Toast/toast.js";
import { motion, AnimatePresence } from "framer-motion";

const Chat = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { language } = useLanguage();
    const [conversations, setConversations] = useState([]);
    const [messages, setMessages] = useState([]);
    const [selectedId, setSelectedId] = useState(location.state?.selectedCustomerId || null);
    const [searchQuery, setSearchQuery] = useState('');
    const [messageInput, setMessageInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [messagesLoading, setMessagesLoading] = useState(false);
    const [activeOrders, setActiveOrders] = useState([]);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [showScrollBottom, setShowScrollBottom] = useState(false);
    const showScrollBottomRef = useRef(false);
    const [unreadSinceScroll, setUnreadSinceScroll] = useState(0);

    const messagesEndRef = useRef(null);
    const chatContainerRef = useRef(null);
    const fileInputRef = useRef(null);
    const toastRef = useRef(null);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const scrollToBottom = (behavior = 'smooth') => {
        messagesEndRef.current?.scrollIntoView({ behavior });
        setUnreadSinceScroll(0);
        setShowScrollBottom(false);
    };

    const handleScroll = (e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
        
        setShowScrollBottom(!isNearBottom);
        showScrollBottomRef.current = !isNearBottom;
        if (isNearBottom) {
            setUnreadSinceScroll(0);
        }
    };

    useEffect(() => {
        fetchChats();
    }, []);

    useEffect(() => {
        if (location.state?.selectedCustomerId) {
            setSelectedId(location.state.selectedCustomerId);
        }
    }, [location.state?.selectedCustomerId]);

    useEffect(() => {
        if (selectedId) {
            fetchMessages(selectedId);
            fetchActiveOrders(selectedId);
        } else {
            setMessages([]);
            setActiveOrders([]);
        }
    }, [selectedId]);

    useEffect(() => {
        // Only auto-scroll if we are already near the bottom or it's our own message
        const lastMessage = messages[messages.length - 1];
        if (lastMessage && (lastMessage.direction === 'CHEF_TO_CUSTOMER' || !showScrollBottom)) {
            scrollToBottom('auto');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [messages.length]);

    const playNotificationSound = useCallback(async () => {
        try {
            const AudioContext = window.AudioContext || window.webkitAudioContext;
            if (!AudioContext) return;
            
            const context = new AudioContext();
            
            // Critical for some browsers: resume context if suspended
            if (context.state === 'suspended') {
                await context.resume();
            }

            const oscillator = context.createOscillator();
            const gainNode = context.createGain();

            oscillator.type = 'sine';
            oscillator.frequency.setValueAtTime(880, context.currentTime); // A5
            
            gainNode.gain.setValueAtTime(0, context.currentTime);
            gainNode.gain.linearRampToValueAtTime(0.2, context.currentTime + 0.05);
            gainNode.gain.exponentialRampToValueAtTime(0.01, context.currentTime + 0.5);

            oscillator.connect(gainNode);
            gainNode.connect(context.destination);

            oscillator.start(context.currentTime);
            oscillator.stop(context.currentTime + 0.5);
            
            // Clean up context after sound to prevent resource leaks
            setTimeout(() => {
                if (context.state !== 'closed') {
                    context.close();
                }
            }, 600);
        } catch (e) {
            console.log('Sound generation blocked or failed:', e);
        }
    }, []);

    const fetchChats = async () => {
        setLoading(true);
        try {
            const data = await chatService.getChats();
            setConversations(data || []);
        } catch (error) {
            console.error("Failed to fetch chats", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchMessages = async (customerId) => {
        setMessagesLoading(true);
        try {
            const data = await chatService.getMessages(customerId);
            setMessages(data || []);
        } catch (error) {
            console.error("Failed to fetch messages", error);
        } finally {
            setMessagesLoading(false);
        }
    };

    const fetchActiveOrders = async (customerId) => {
        try {
            const data = await getActiveOrdersByCustomer(customerId);
            setActiveOrders(data || []);
        } catch (error) {
            console.error("Failed to fetch active orders", error);
            setActiveOrders([]);
        }
    };

    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (!messageInput.trim() || !selectedId) return;
        
        const text = messageInput;
        setMessageInput('');
        
        try {
            const newMessage = await chatService.sendMessage(selectedId, text);
            setMessages(prev => [...prev, newMessage]);
            
            // Update last message in the list
            setConversations(prev => prev.map(c => 
                c.customerId === selectedId 
                    ? { ...c, lastMessage: text, lastMessageAt: new Date().toISOString() } 
                    : c
            ));
        } catch (error) {
            toastRef.current?.addToast("Failed to send message", "error");
            setMessageInput(text); // restore if failed
        }
    };

    const handleBroadcastMenu = async () => {
        try {
            await chatService.broadcastMenu();
            toastRef.current?.addToast(getTranslation('chat.broadcastSuccess', language), "success");
        } catch (error) {
            toastRef.current?.addToast("Failed to broadcast menu", "error");
        }
    };

    const filteredConversations = useMemo(() => {
        return conversations.filter(c => 
            c.customerName?.toLowerCase().includes(searchQuery.toLowerCase())
        );
    }, [searchQuery, conversations]);

    const parseUTCDate = (dateString) => {
        if (!dateString) return null;
        if (dateString.endsWith('Z') || dateString.includes('+')) {
            return new Date(dateString);
        }
        return new Date(dateString + 'Z');
    };

    /**
     * Format time for the sidebar list (Telegram-style)
     */
    const formatChatTime = (dateString) => {
        if (!dateString) return '';
        const date = parseUTCDate(dateString);
        if (!date || isNaN(date.getTime())) return '';
        
        const now = new Date();
        const isToday = date.toDateString() === now.toDateString();
        
        if (isToday) {
            return date.toLocaleTimeString([], { hour12: false, hour: '2-digit', minute: '2-digit' });
        }
        
        const yesterday = new Date(now);
        yesterday.setDate(now.getDate() - 1);
        if (date.toDateString() === yesterday.toDateString()) {
            return getTranslation('common.yesterday', language) || 'Yesterday';
        }
        
        const diffInDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
        if (diffInDays < 7) {
            return date.toLocaleDateString([], { weekday: 'short' });
        }
        
        if (date.getFullYear() === now.getFullYear()) {
            return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
        }
        
        return date.toLocaleDateString([], { year: 'numeric', month: 'short', day: 'numeric' });
    };

    /**
     * Format time for message bubbles (24h)
     */
    const formatMessageTime = (dateString) => {
        const date = parseUTCDate(dateString);
        if (!date || isNaN(date.getTime())) return '';
        return date.toLocaleTimeString([], { hour12: false, hour: '2-digit', minute: '2-digit' });
    };

    /**
     * Get date header for message grouping
     */
    const getMessageDateHeader = (dateString) => {
        const date = parseUTCDate(dateString);
        if (!date || isNaN(date.getTime())) return '';
        
        const now = new Date();
        if (date.toDateString() === now.toDateString()) {
            return getTranslation('common.today', language) || 'Today';
        }
        
        const yesterday = new Date(now);
        yesterday.setDate(now.getDate() - 1);
        if (date.toDateString() === yesterday.toDateString()) {
            return getTranslation('common.yesterday', language) || 'Yesterday';
        }
        
        const options = { month: 'long', day: 'numeric' };
        if (date.getFullYear() !== now.getFullYear()) {
            options.year = 'numeric';
        }
        return date.toLocaleDateString(language === 'arm' ? 'hy-AM' : language === 'ru' ? 'ru-RU' : 'en-US', options);
    };

    // Layout fix to prevent outer scroll
    useEffect(() => {
        const mainElement = document.querySelector('main');
        if (mainElement) {
            const originalOverflow = mainElement.style.overflow;
            mainElement.style.overflow = 'hidden';
            return () => {
                mainElement.style.overflow = originalOverflow;
            };
        }
    }, []);

    // Mark as read when messages change or chat is selected
    useEffect(() => {
        if (selectedId && messages.length > 0) {
            const hasUnread = messages.some(m => m.direction === 'CUSTOMER_TO_CHEF' && !m.isReadByChef);
            if (hasUnread) {
                // Optimistic update for messages
                setMessages(prev => prev.map(m => 
                    m.direction === 'CUSTOMER_TO_CHEF' ? { ...m, isReadByChef: true } : m
                ));
                
                // Optimistic update for conversation list
                setConversations(prev => prev.map(c => 
                    c.customerId === selectedId ? { ...c, unreadCount: 0 } : c
                ));

                chatService.markAsRead(selectedId).catch(err => {
                    console.error('Failed to mark as read:', err);
                });
            }
        }
    }, [selectedId, messages]); // Trigger when selected chat or messages change

    // Polling for real-time updates
    useEffect(() => {
        const pollMessages = async () => {
            if (selectedId && document.visibilityState === 'visible' && !messagesLoading) {
                try {
                    const data = await chatService.getMessages(selectedId);
                    setMessages(prev => {
                        if (JSON.stringify(prev) !== JSON.stringify(data)) {
                            // If new messages and user is scrolled up, increment unread counter for the button
                            if (data.length > prev.length) {
                                const newCount = data.length - prev.length;
                                
                                // Play sound for new incoming messages from customer
                                const lastNewMsg = data[data.length - 1];
                                if (lastNewMsg && lastNewMsg.direction === 'CUSTOMER_TO_CHEF') {
                                    playNotificationSound();
                                }

                                if (showScrollBottomRef.current) {
                                    setUnreadSinceScroll(u => u + newCount);
                                }
                            }
                            return data;
                        }
                        return prev;
                    });
                } catch (error) {
                    console.error('Polling messages failed:', error);
                }
            }
        };

        const pollChats = async () => {
            if (document.visibilityState === 'visible') {
                try {
                    const data = await chatService.getChats();
                    setConversations(prev => {
                        if (JSON.stringify(prev) !== JSON.stringify(data)) return data;
                        return prev;
                    });
                } catch (error) {
                    console.error('Polling chats failed:', error);
                }
            }
        };

        const messageInterval = setInterval(pollMessages, 5000); 
        const chatInterval = setInterval(pollChats, 20000); 

        return () => {
            clearInterval(messageInterval);
            clearInterval(chatInterval);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedId, messagesLoading, playNotificationSound]);

    // Helper to highlight search matches
    const HighlightText = ({ text, highlight }) => {
        if (!highlight.trim()) return <span>{text}</span>;
        
        try {
            const regex = new RegExp(`(${highlight})`, 'gi');
            const parts = text.split(regex);
            
            return (
                <span>
                    {parts.map((part, i) => 
                        regex.test(part) ? (
                            <span key={i} className="bg-orange-500/20 text-orange-600 dark:text-orange-400 font-bold rounded-sm px-0.5">
                                {part}
                            </span>
                        ) : (
                            part
                        )
                    )}
                </span>
            );
        } catch (e) {
            return <span>{text}</span>;
        }
    };

    const handleFileClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            console.log('Selected file:', file.name);
            toastRef.current?.addToast("Photo sending is not implemented with backend yet", "error");
        }
    };

    const activeCustomer = conversations.find(c => c.customerId === selectedId);

    return (
        <div 
            className="flex absolute inset-0 bg-white dark:bg-[#0d1117] overflow-hidden z-0"
        >
            <input 
                type="file" 
                ref={fileInputRef} 
                className="hidden" 
                onChange={handleFileChange}
                accept="image/*"
            />

            {/* Sidebar / Chat List */}
            <div className={`${isMobile && selectedId ? 'hidden' : 'flex'} ${isMobile ? 'w-full' : 'w-auto min-w-[280px] max-w-[350px]'} border-r border-gray-200 dark:border-[#30363d] flex-col h-full bg-white dark:bg-[#0d1117]`}>
                <div className="p-4 border-b border-gray-100 dark:border-[#30363d]">
                    <div className="flex flex-col gap-3">
                        <button
                            onClick={handleBroadcastMenu}
                            className="w-full flex items-center justify-center gap-2 py-2 px-4 bg-gray-900 dark:bg-[#EA580C] text-white text-xs font-bold rounded-lg hover:opacity-90 transition-opacity whitespace-nowrap"
                        >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                            </svg>
                            {getTranslation('chat.broadcastMenu', language)}
                        </button>
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <svg className="h-4 w-4 text-gray-400 group-focus-within:text-orange-500 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                            </div>
                            <input
                                type="text"
                                placeholder={getTranslation('chat.searchPlaceholder', language)}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-[#161b22] border border-gray-100 dark:border-[#30363d] rounded-xl text-sm text-gray-900 dark:text-[#c9d1d9] focus:ring-1 focus:ring-orange-500/30 focus:border-orange-500/50 outline-none transition-all"
                            />
                        </div>
                    </div>
                </div>
                
                <div className="flex-1 overflow-y-auto">
                    {loading ? (
                        <div className="p-8 flex justify-center"><LoadingSpinner /></div>
                    ) : filteredConversations.length > 0 ? (
                        filteredConversations.map((conv) => (
                            <div
                                key={conv.customerId}
                                onClick={() => setSelectedId(conv.customerId)}
                                className={`p-4 flex items-center cursor-pointer hover:bg-gray-50 dark:hover:bg-[#1c2128] transition-colors border-b border-gray-50 dark:border-[#30363d] ${selectedId === conv.customerId ? 'bg-gray-100 dark:bg-[#1c2128]' : ''}`}
                            >
                                <div className="w-12 h-12 rounded-full bg-gray-200 dark:bg-[#21262d] flex items-center justify-center text-gray-600 dark:text-[#8b949e] font-bold text-lg mr-3 shrink-0 uppercase">
                                    {conv.customerName?.charAt(0)}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex justify-between items-center mb-1 gap-2">
                                        <h3 className="text-xs font-bold text-gray-900 dark:text-[#c9d1d9] truncate">
                                            <HighlightText text={conv.customerName} highlight={searchQuery} />
                                        </h3>
                                        <span className="text-[10px] text-gray-400 dark:text-[#8b949e]">
                                            {formatChatTime(conv.lastMessageAt)}
                                        </span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <p className="text-xs text-gray-500 dark:text-[#8b949e] truncate leading-relaxed">
                                            <HighlightText text={conv.lastMessage || '...'} highlight={searchQuery} />
                                        </p>
                                        {conv.unreadCount > 0 && (
                                            <span className="bg-gray-900 dark:bg-[#EA580C] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full ml-2 shrink-0">
                                                {conv.unreadCount}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="p-8 text-center text-sm text-gray-400">Nothing found</div>
                    )}
                </div>
            </div>

            {/* Main Chat Area */}
            <div className={`${isMobile && !selectedId ? 'hidden' : 'flex'} flex-1 flex flex-col bg-gray-50/30 dark:bg-[#0d1117] h-full`}>
                {selectedId ? (
                    <>
                        {/* Chat Header */}
                        <div className="p-4 bg-white dark:bg-[#161b22] border-b border-gray-200 dark:border-[#30363d] flex items-center shadow-sm z-10 sticky top-0">
                            {isMobile && (
                                <button 
                                    onClick={() => setSelectedId(null)}
                                    className="p-2 mr-2 -ml-2 text-gray-500 hover:text-gray-900 dark:hover:text-[#c9d1d9]"
                                >
                                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                    </svg>
                                </button>
                            )}
                            <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-[#21262d] flex items-center justify-center text-gray-600 dark:text-[#8b949e] font-bold mr-3 uppercase">
                                {activeCustomer?.customerName?.charAt(0)}
                            </div>
                            <div className="flex-1 min-w-0">
                                <h3 className="text-sm font-bold text-gray-900 dark:text-[#c9d1d9] truncate">
                                    {activeCustomer?.customerName}
                                </h3>
                                <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                                    <p className="text-[10px] text-gray-500 dark:text-[#8b949e] font-medium">{activeCustomer?.phoneNumber}</p>
                                    {activeCustomer?.companyName?.trim() && (
                                        <span className="text-[10px] text-orange-500 dark:text-orange-400 font-semibold truncate max-w-[200px]">
                                            {activeCustomer.companyName.trim()}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Active Orders Strip */}
                        {activeOrders.length > 0 && (
                            <div className="bg-white dark:bg-[#161b22] border-b border-gray-200 dark:border-[#30363d] px-3 py-2">
                                <p className="text-[9px] font-bold uppercase tracking-wider text-gray-400 dark:text-[#8b949e] mb-1.5">
                                    {getTranslation('chat.activeOrders', language) || 'Active Orders'}
                                </p>
                                <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
                                    {activeOrders.map(order => (
                                        <button
                                            key={order.id}
                                            onClick={() => navigate(`/orders/${order.id}`)}
                                            className="flex-shrink-0 flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-[#0d1117] border border-gray-200 dark:border-[#30363d] rounded-xl hover:border-orange-400 dark:hover:border-orange-500 hover:bg-orange-50/50 dark:hover:bg-orange-500/5 transition-all group"
                                        >
                                            <div className="flex flex-col items-start">
                                                <span className="text-[10px] font-bold text-gray-700 dark:text-[#c9d1d9] group-hover:text-orange-600 dark:group-hover:text-orange-400 transition-colors">
                                                    {order.number}
                                                </span>
                                                <span className={`text-[9px] font-semibold mt-0.5 px-1.5 py-0.5 rounded-full ${
                                                    order.status === 'REQUESTED' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' :
                                                    order.status === 'PREPARING' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                                                    order.status === 'READY_FOR_DELIVERY' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                                                    order.status === 'DELIVERY_IN_PROGRESS' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' :
                                                    'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                                                }`}>
                                                    {getTranslation(`orderStatuses.${order.status?.toLowerCase()}`, language) || order.status?.replace('_', ' ')}
                                                </span>
                                            </div>
                                            <span className="text-[10px] font-bold text-gray-500 dark:text-[#8b949e] whitespace-nowrap">
                                                {order.totalPrice != null ? `֏${Number(order.totalPrice).toLocaleString()}` : ''}
                                            </span>
                                            <svg className="w-3 h-3 text-gray-300 dark:text-[#484f58] group-hover:text-orange-400 flex-shrink-0 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                                            </svg>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Messages List */}
                        <div 
                            ref={chatContainerRef}
                            onScroll={handleScroll}
                            className="flex-1 overflow-y-auto p-4 space-y-4 relative"
                        >
                            {messagesLoading ? (
                                <div className="flex justify-center p-4"><LoadingSpinner /></div>
                            ) : messages.length > 0 ? (
                                messages.map((msg, index) => {
                                    const showDateHeader = index === 0 || 
                                        parseUTCDate(messages[index - 1].createdAt).toDateString() !== parseUTCDate(msg.createdAt).toDateString();
                                    
                                    return (
                                        <motion.div 
                                            key={msg.id}
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.3 }}
                                        >
                                            {showDateHeader && (
                                                <div className="flex justify-center my-4">
                                                    <span className="bg-gray-200/50 dark:bg-[#21262d] text-gray-500 dark:text-[#8b949e] text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-wider backdrop-blur-sm shadow-sm">
                                                        {getMessageDateHeader(msg.createdAt)}
                                                    </span>
                                                </div>
                                            )}
                                            <div 
                                                className={`flex ${msg.direction === 'CHEF_TO_CUSTOMER' ? 'justify-end' : 'justify-start'}`}
                                            >
                                                <div className={`max-w-[85%] sm:max-w-[70%] rounded-2xl p-3 shadow-sm relative ${
                                                    msg.direction === 'CHEF_TO_CUSTOMER' 
                                                        ? 'bg-gray-900 text-white rounded-tr-none' 
                                                        : `bg-white dark:bg-[#1c2128] text-gray-900 dark:text-[#c9d1d9] border rounded-tl-none ${
                                                            msg.direction === 'CUSTOMER_TO_CHEF' && !msg.isReadByChef 
                                                                ? 'border-orange-500/30 ring-1 ring-orange-500/10' 
                                                                : 'border-gray-100 dark:border-[#30363d]'
                                                        }`
                                                }`}>
                                                    <p className="text-sm leading-relaxed">{msg.messageText}</p>
                                                    <div className={`text-[9px] mt-1.5 flex items-center justify-end ${
                                                        msg.direction === 'CHEF_TO_CUSTOMER' ? 'text-gray-400' : 'text-gray-500'
                                                    }`}>
                                                        {formatMessageTime(msg.createdAt)}
                                                        {msg.direction === 'CUSTOMER_TO_CHEF' && !msg.isReadByChef && (
                                                            <span className="ml-1.5 w-1.5 h-1.5 bg-orange-500 rounded-full inline-block" title="Unread"></span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </motion.div>
                                    );
                                })
                            ) : (
                                <div className="flex-1 flex items-center justify-center text-gray-400 text-xs italic p-10">
                                    {getTranslation('chat.noMessages', language)}
                                </div>
                            )}
                            <div ref={messagesEndRef} />

                            {/* Scroll to bottom button */}
                            <AnimatePresence>
                                {showScrollBottom && (
                                    <motion.button
                                        initial={{ opacity: 0, scale: 0.8, y: 20 }}
                                        animate={{ opacity: 1, scale: 1, y: 0 }}
                                        exit={{ opacity: 0, scale: 0.8, y: 20 }}
                                        onClick={() => scrollToBottom('smooth')}
                                        className="absolute bottom-6 right-6 w-10 h-10 bg-white dark:bg-[#161b22] border border-gray-200 dark:border-[#30363d] rounded-full shadow-lg flex items-center justify-center text-gray-500 dark:text-[#8b949e] hover:text-gray-900 dark:hover:text-[#c9d1d9] z-20 group"
                                    >
                                        <svg className="w-5 h-5 transition-transform group-hover:translate-y-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7-7-7" />
                                        </svg>
                                        {unreadSinceScroll > 0 && (
                                            <span className="absolute -top-1 -right-1 bg-orange-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full ring-2 ring-white dark:ring-[#0d1117]">
                                                {unreadSinceScroll}
                                            </span>
                                        )}
                                    </motion.button>
                                )}
                            </AnimatePresence>
                        </div>

                        {/* Input Area */}
                        <div className="p-3 sm:p-4 bg-white dark:bg-[#161b22] border-t border-gray-200 dark:border-[#30363d] sticky bottom-0">
                            <form onSubmit={handleSendMessage} className="flex items-center gap-2 max-w-full">
                                <button 
                                    type="button" 
                                    onClick={handleFileClick}
                                    className="w-10 h-10 flex items-center justify-center text-gray-500 hover:text-gray-900 dark:hover:text-[#c9d1d9] rounded-full transition-colors flex-shrink-0"
                                >
                                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                    </svg>
                                </button>
                                <div className="flex-1 relative min-w-0 flex items-center">
                                    <textarea
                                        rows="1"
                                        placeholder={getTranslation('chat.typeMessage', language)}
                                        className="w-full pl-4 pr-12 py-3 bg-gray-50 dark:bg-[#0d1117] border border-gray-200 dark:border-[#30363d] rounded-2xl focus:ring-1 focus:ring-gray-300 dark:focus:ring-[#484f58] outline-none text-sm text-gray-900 dark:text-[#c9d1d9] resize-none overflow-hidden block"
                                        style={{ minHeight: '44px', maxHeight: '120px' }}
                                        value={messageInput}
                                        onChange={(e) => {
                                            setMessageInput(e.target.value);
                                            e.target.style.height = 'auto';
                                            e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`;
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' && !e.shiftKey) {
                                                e.preventDefault();
                                                handleSendMessage(e);
                                                e.target.style.height = 'auto';
                                            }
                                        }}
                                    />
                                    <button 
                                        type="submit"
                                        disabled={!messageInput.trim()}
                                        className="absolute right-1.5 p-2 bg-gray-900 dark:bg-[#EA580C] text-white rounded-full disabled:opacity-50 disabled:cursor-not-allowed hover:scale-105 active:scale-95 transition-all shadow-md flex items-center justify-center"
                                        style={{ width: '32px', height: '32px' }}
                                    >
                                        <svg className="h-4 w-4 transform rotate-90" fill="currentColor" viewBox="0 0 20 20">
                                            <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
                                        </svg>
                                    </button>
                                </div>
                            </form>
                        </div>
                    </>
                ) : (
                    <div className="flex-1 flex flex-col items-center justify-center p-8 text-center bg-gray-50/50 dark:bg-[#0d1117]">
                        <div className="w-20 h-20 sm:w-24 sm:h-24 bg-gray-100 dark:bg-[#161b22] rounded-full flex items-center justify-center mb-6 shadow-inner">
                            <svg className="h-10 w-10 sm:h-12 sm:w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                            </svg>
                        </div>
                        <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-[#c9d1d9] mb-2 leading-tight">
                            {getTranslation('chat.selectConversation', language)}
                        </h3>
                        <p className="text-xs sm:text-sm text-gray-500 dark:text-[#8b949e]">{getTranslation('chat.selectConversationSubtitle', language)}</p>
                    </div>
                )}
            </div>
            <Toast ref={toastRef} />
        </div>
    );
};

export default Chat;
